import { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Loader2, Trash2, Tag, Briefcase, Sparkles, X, CreditCard, MessageSquare, Send, Megaphone, Upload, Pause, Play } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth0Safe as useAuth0 } from '@/hooks/useAuth0Safe';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_LABEL = {
  pending: { text: 'Na pregledu', className: 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300' },
  active: { text: 'Aktivan', className: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-300' },
  rejected: { text: 'Odbijen', className: 'bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-300' },
};

function isPromoted(item) {
  if (!item?.promoted_until) return false;
  try { return new Date(item.promoted_until) > new Date(); } catch { return false; }
}

function PromoteModal({ kind, item, onClose, onPaid }) {
  const { getAccessTokenSilently } = useAuth0();
  const [tiers, setTiers] = useState([]);
  const [tierIdx, setTierIdx] = useState(0);
  const [provider, setProvider] = useState('stripe');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    fetch(`${API}/public/listing-promotion-settings`)
      .then(r => r.ok ? r.json() : null)
      .then(d => { if (d?.tiers) setTiers(d.tiers); })
      .catch(() => {});
  }, []);

  async function startCheckout() {
    setBusy(true);
    try {
      const token = await getAccessTokenSilently();
      const r = await fetch(`${API}/me/${kind}/${item.id}/promote`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ tier_index: tierIdx, provider }),
      });
      const d = await r.json();
      if (!r.ok || !d.url) throw new Error(d?.detail || 'Checkout neuspješan');
      window.location.href = d.url;
    } catch (e) {
      toast.error('Greška pri kreiranju plaćanja: ' + (e.message || e));
      setBusy(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4" onClick={onClose} data-testid="promote-modal">
      <div className="bg-white dark:bg-[#0f172a] rounded-2xl max-w-md w-full p-6" onClick={e => e.stopPropagation()}>
        <div className="flex items-start justify-between mb-3">
          <div>
            <h3 className="text-lg font-bold flex items-center gap-2 text-gray-900 dark:text-gray-100">
              <Sparkles size={18} className="text-amber-500" /> Istakni oglas
            </h3>
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1 line-clamp-1">{item.title}</p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-700 dark:hover:text-white"><X size={18} /></button>
        </div>

        <p className="text-xs font-medium text-gray-700 dark:text-gray-300 mb-2">Odaberi paket</p>
        <div className="grid gap-2 mb-4">
          {tiers.map((t, i) => (
            <button
              key={i}
              onClick={() => setTierIdx(i)}
              className={`flex items-center justify-between px-4 py-3 rounded-lg border-2 text-left transition ${tierIdx === i ? 'border-amber-500 bg-amber-50 dark:bg-amber-900/20' : 'border-gray-200 dark:border-gray-700 bg-white dark:bg-[#0b1220]'}`}
              data-testid={`promote-tier-${t.days}`}
            >
              <span className="text-sm font-semibold text-gray-900 dark:text-gray-100">{t.label || `${t.days} dana`}</span>
              <span className="text-base font-bold text-amber-600 dark:text-amber-400">{t.price_eur.toFixed(2)} €</span>
            </button>
          ))}
        </div>

        <p className="text-xs font-medium text-gray-700 dark:text-gray-300 mb-2">Način plaćanja</p>
        <div className="grid grid-cols-2 gap-2 mb-5">
          <button onClick={() => setProvider('stripe')}
            className={`py-2 rounded-lg text-xs font-semibold border ${provider === 'stripe' ? 'bg-amber-500 text-white border-amber-500' : 'bg-white dark:bg-[#0b1220] text-gray-700 dark:text-gray-200 border-gray-200 dark:border-gray-700'}`}
            data-testid="promote-provider-stripe">Stripe</button>
          <button onClick={() => setProvider('paypal')}
            className={`py-2 rounded-lg text-xs font-semibold border ${provider === 'paypal' ? 'bg-amber-500 text-white border-amber-500' : 'bg-white dark:bg-[#0b1220] text-gray-700 dark:text-gray-200 border-gray-200 dark:border-gray-700'}`}
            data-testid="promote-provider-paypal">PayPal</button>
        </div>

        <button onClick={startCheckout} disabled={busy || tiers.length === 0}
          className="w-full py-2.5 bg-amber-500 hover:bg-amber-600 text-white text-sm font-bold rounded-lg flex items-center justify-center gap-2 disabled:opacity-50"
          data-testid="promote-checkout-btn">
          {busy ? <Loader2 className="animate-spin" size={14} /> : <CreditCard size={14} />}
          Plati i istakni
        </button>
        <p className="text-[10px] text-gray-500 dark:text-gray-400 text-center mt-2">
          Plaćanjem se oglas prikazuje na vrhu popisa s zlatnim okvirom i oznakom <b>ISTAKNUTO</b> tijekom odabranog perioda.
        </p>
      </div>
    </div>
  );
}

const BACKEND = process.env.REACT_APP_BACKEND_URL;

const SCALING_OPTS = [
  { value: 'cover', label: 'Obreži (zadrži omjer)' },
  { value: 'contain', label: 'Uklopi (s prazninama)' },
  { value: 'stretch', label: 'Razvuci (može deformirati)' },
];
const STATUS_BADGE_AD = {
  active: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-300',
  paused: 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300',
  pending_media: 'bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-300',
  expired: 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300',
  deleted: 'bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-300',
  replaced: 'bg-fuchsia-100 text-fuchsia-800 dark:bg-fuchsia-900/40 dark:text-fuchsia-300',
};

function MyAdPlacementCard({ placement, getToken, onChanged }) {
  const [p, setP] = useState(placement);
  const [busy, setBusy] = useState(false);
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({ title: p.title || '', body: p.body || '', target_url: p.target_url || '', scaling_mode: p.scaling_mode || 'cover' });
  const [showScalingHint, setShowScalingHint] = useState(false);

  function fixUrl(u) { return u ? (u.startsWith('http') ? u : `${BACKEND}${u}`) : ''; }

  async function call(path, opts = {}) {
    setBusy(true);
    try {
      const token = await getToken();
      const headers = { Authorization: `Bearer ${token}`, ...(opts.headers || {}) };
      const r = await fetch(`${API}${path}`, { ...opts, headers });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      return await r.json();
    } finally { setBusy(false); }
  }

  async function uploadMedia(file) {
    if (!file) return;
    if (file.size > 100 * 1024 * 1024) { toast.error('Datoteka prelazi 100 MB.'); return; }
    setShowScalingHint(true);
    const fd = new FormData();
    fd.append('file', file);
    fd.append('scaling_mode', form.scaling_mode || 'cover');
    try {
      const d = await call(`/me/ads/placements/${p.id}/media`, { method: 'POST', body: fd });
      setP({ ...p, media_url: d.media_url, media_type: d.media_type, status: d.status });
      toast.success('Medij učitan');
      onChanged?.();
    } catch (e) { toast.error('Upload neuspješan'); }
  }

  async function saveText() {
    try {
      await call(`/me/ads/placements/${p.id}`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ title: form.title, body: form.body, target_url: form.target_url, scaling_mode: form.scaling_mode }),
      });
      setP({ ...p, ...form });
      setEditing(false);
      toast.success('Spremljeno');
      onChanged?.();
    } catch { toast.error('Greška'); }
  }

  async function togglePause() {
    try {
      const which = p.status === 'active' ? 'pause' : 'resume';
      await call(`/me/ads/placements/${p.id}/${which}`, { method: 'POST' });
      setP({ ...p, status: which === 'pause' ? 'paused' : (p.media_url ? 'active' : 'pending_media') });
      onChanged?.();
    } catch { toast.error('Greška'); }
  }

  async function deleteAd() {
    if (!window.confirm('Sigurno obrisati oglas?')) return;
    try {
      await call(`/me/ads/placements/${p.id}`, { method: 'DELETE' });
      toast.success('Obrisano');
      onChanged?.();
    } catch { toast.error('Greška'); }
  }

  const objectFit = p.scaling_mode === 'stretch' ? 'fill' : (p.scaling_mode === 'contain' ? 'contain' : 'cover');

  return (
    <li data-testid={`my-ad-${p.id}`} className="bg-white dark:bg-[#111827] rounded-xl border border-gray-200 dark:border-gray-800 p-4">
      <div className="flex gap-4 items-start">
        <div className="w-32 h-20 bg-gray-100 dark:bg-zinc-900 rounded overflow-hidden flex-shrink-0 border border-gray-200 dark:border-zinc-800 flex items-center justify-center">
          {p.media_url ? (
            p.media_type === 'video' ? (
              <video src={fixUrl(p.media_url)} muted className="w-full h-full" style={{ objectFit }} />
            ) : (
              <img src={fixUrl(p.media_url)} alt="" className="w-full h-full" style={{ objectFit }} />
            )
          ) : (
            <span className="text-[10px] text-gray-400">Bez medija</span>
          )}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap mb-1">
            <span className={`text-[10px] uppercase font-bold px-2 py-0.5 rounded ${STATUS_BADGE_AD[p.status] || 'bg-gray-100 text-gray-700'}`}>{p.status}</span>
            <span className="text-[10px] text-gray-500">Slot: {p.slot} · {p.ad_kind}</span>
            {p.end_at && <span className="text-[10px] text-gray-500">Vrijedi do {new Date(p.end_at).toLocaleDateString('hr')}</span>}
          </div>
          {editing ? (
            <div className="space-y-2">
              <input value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} placeholder="Naslov" className="w-full bg-gray-50 dark:bg-zinc-950 border border-gray-300 dark:border-zinc-700 rounded px-2 py-1 text-sm" data-testid={`ad-title-${p.id}`} />
              <textarea value={form.body} onChange={e => setForm({ ...form, body: e.target.value })} rows={2} placeholder="Tekst" className="w-full bg-gray-50 dark:bg-zinc-950 border border-gray-300 dark:border-zinc-700 rounded px-2 py-1 text-sm" data-testid={`ad-body-${p.id}`} />
              <input value={form.target_url} onChange={e => setForm({ ...form, target_url: e.target.value })} placeholder="Cilj URL" className="w-full bg-gray-50 dark:bg-zinc-950 border border-gray-300 dark:border-zinc-700 rounded px-2 py-1 text-sm" data-testid={`ad-target-${p.id}`} />
              <div className="flex gap-2">
                <button onClick={saveText} disabled={busy} className="px-3 py-1.5 text-xs bg-red-600 hover:bg-red-700 text-white rounded font-bold" data-testid={`ad-save-${p.id}`}>Spremi</button>
                <button onClick={() => setEditing(false)} className="px-3 py-1.5 text-xs text-gray-500 hover:text-gray-900">Odustani</button>
              </div>
            </div>
          ) : (
            <>
              <p className="font-bold text-gray-900 dark:text-gray-100 truncate">{p.title || '(bez naslova)'}</p>
              {p.body && <p className="text-xs text-gray-600 dark:text-gray-400 mt-0.5 line-clamp-2">{p.body}</p>}
              {p.target_url && <p className="text-xs text-blue-600 dark:text-blue-400 mt-0.5 truncate">{p.target_url}</p>}
            </>
          )}
        </div>
      </div>

      <div className="mt-3 pt-3 border-t border-gray-200 dark:border-zinc-800 flex flex-wrap items-center gap-2">
        <div className="flex items-center gap-1.5">
          <label className="text-[10px] uppercase text-gray-500">Skaliranje:</label>
          <select value={form.scaling_mode} onChange={e => setForm({ ...form, scaling_mode: e.target.value })}
                  data-testid={`ad-scaling-${p.id}`}
                  className="bg-gray-50 dark:bg-zinc-950 border border-gray-300 dark:border-zinc-700 rounded px-2 py-1 text-xs">
            {SCALING_OPTS.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
          </select>
        </div>
        <label className="flex items-center gap-1 px-3 py-1.5 text-xs bg-blue-600 hover:bg-blue-700 text-white rounded cursor-pointer font-semibold" data-testid={`ad-upload-${p.id}`}>
          <Upload size={12} /> {p.media_url ? 'Zamijeni medij' : 'Učitaj medij'}
          <input type="file" accept="image/*,video/*" className="hidden" onChange={e => uploadMedia(e.target.files?.[0])} />
        </label>
        {!editing && (
          <button onClick={() => setEditing(true)} className="px-3 py-1.5 text-xs bg-gray-100 dark:bg-zinc-800 hover:bg-gray-200 dark:hover:bg-zinc-700 text-gray-800 dark:text-gray-200 rounded" data-testid={`ad-edit-${p.id}`}>
            Uredi tekst
          </button>
        )}
        {(p.status === 'active' || p.status === 'paused') && (
          <button onClick={togglePause} disabled={busy}
                  data-testid={`ad-toggle-${p.id}`}
                  className="flex items-center gap-1 px-3 py-1.5 text-xs bg-amber-500 hover:bg-amber-600 text-white rounded font-semibold">
            {p.status === 'active' ? <><Pause size={12} /> Pauziraj</> : <><Play size={12} /> Nastavi</>}
          </button>
        )}
        <button onClick={deleteAd} disabled={busy}
                data-testid={`ad-delete-${p.id}`}
                className="ml-auto p-2 text-red-500 hover:text-red-700">
          <Trash2 size={14} />
        </button>
      </div>
      {showScalingHint && (
        <p className="mt-2 text-[10px] text-amber-700 dark:text-amber-400 italic">
          ⚠️ Ako dimenzije medija ne odgovaraju odabranom slotu, primijenit će se izabrani način skaliranja ({form.scaling_mode}).
        </p>
      )}
    </li>
  );
}

export default function MyListingsPage() {
  const { isAuthenticated, getAccessTokenSilently, loginWithRedirect } = useAuth0();
  const [classifieds, setClassifieds] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [conversations, setConversations] = useState([]);
  const [adPlacements, setAdPlacements] = useState([]);
  const [activeConv, setActiveConv] = useState(null);
  const [convMessages, setConvMessages] = useState([]);
  const [reply, setReply] = useState('');
  const [loading, setLoading] = useState(true);
  const [promoteFor, setPromoteFor] = useState(null); // { kind, item }
  const location = useLocation();
  const navigate = useNavigate();

  // Stripe success callback — backend confirms payment then bumps promoted_until.
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const paid = params.get('promoted');
    const orderId = params.get('order');
    const sessionId = params.get('session_id');
    if (paid === '1' && orderId && sessionId) {
      fetch(`${API}/public/listing-promotion/confirm?session_id=${encodeURIComponent(sessionId)}&order_id=${encodeURIComponent(orderId)}`)
        .then(r => r.ok ? r.json() : null)
        .then(d => {
          if (d?.ok) toast.success('Oglas je istaknut!');
          // Clean URL params
          navigate('/moji-oglasi', { replace: true });
        });
    } else if (paid === '1') {
      // PayPal flow already capped the listing — just acknowledge.
      toast.success('Oglas je istaknut!');
      navigate('/moji-oglasi', { replace: true });
    } else if (paid === 'cancel') {
      toast.info('Plaćanje otkazano.');
      navigate('/moji-oglasi', { replace: true });
    }
  }, [location.search, navigate]);

  async function reload() {
    if (!isAuthenticated) { setLoading(false); return; }
    try {
      const token = await getAccessTokenSilently();
      const headers = { Authorization: `Bearer ${token}` };
      const [c, j, m, ads] = await Promise.all([
        fetch(`${API}/me/classifieds`, { headers }).then(r => r.ok ? r.json() : { classifieds: [] }),
        fetch(`${API}/me/jobs`, { headers }).then(r => r.ok ? r.json() : { jobs: [] }),
        fetch(`${API}/me/messages`, { headers }).then(r => r.ok ? r.json() : { conversations: [] }),
        fetch(`${API}/me/ads/placements`, { headers }).then(r => r.ok ? r.json() : { items: [] }),
      ]);
      setClassifieds(c.classifieds || []);
      setJobs(j.jobs || []);
      setConversations(m.conversations || []);
      setAdPlacements(ads.items || []);
    } catch (e) { /* swallow */ } finally { setLoading(false); }
  }

  async function openConv(c) {
    setActiveConv(c);
    try {
      const token = await getAccessTokenSilently();
      const r = await fetch(`${API}/me/messages/${c.conversation_id}`, { headers: { Authorization: `Bearer ${token}` } });
      const d = await r.json();
      setConvMessages(d.messages || []);
    } catch { setConvMessages([]); }
  }

  async function sendReply() {
    if (!reply.trim() || !activeConv) return;
    try {
      const token = await getAccessTokenSilently();
      const r = await fetch(`${API}/me/messages/${activeConv.conversation_id}/reply`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ body: reply.trim() }),
      });
      if (!r.ok) throw new Error('reply failed');
      setReply('');
      openConv(activeConv);
    } catch { toast.error('Slanje neuspješno'); }
  }

  useEffect(() => { reload(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [isAuthenticated]);

  async function deleteItem(kind, id) {
    if (!window.confirm('Sigurno obrisati ovaj oglas?')) return;
    try {
      const token = await getAccessTokenSilently();
      const r = await fetch(`${API}/me/${kind}/${id}`, { method: 'DELETE', headers: { Authorization: `Bearer ${token}` } });
      if (!r.ok) throw new Error('delete failed');
      toast.success('Oglas obrisan');
      if (kind === 'classifieds') setClassifieds(arr => arr.filter(x => x.id !== id));
      else setJobs(arr => arr.filter(x => x.id !== id));
    } catch { toast.error('Brisanje neuspješno'); }
  }

  if (!isAuthenticated) {
    return (
      <div className="max-w-md mx-auto px-4 py-20 text-center" data-testid="my-listings-login">
        <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-3">Prijava potrebna</h1>
        <p className="text-gray-500 dark:text-gray-400 mb-5">Prijavite se za pregled svojih oglasa.</p>
        <button onClick={() => loginWithRedirect()} className="px-6 py-2.5 bg-red-600 hover:bg-red-700 text-white text-sm rounded-lg font-medium">Prijavi se</button>
      </div>
    );
  }

  function PromoteBadge({ until }) {
    if (!until) return null;
    const d = new Date(until);
    const today = new Date();
    const days = Math.max(0, Math.ceil((d - today) / 86400000));
    return (
      <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase text-amber-900 bg-amber-200 dark:bg-amber-300/90 px-2 py-0.5 rounded-full" data-testid="promoted-badge">
        <Sparkles size={10} /> Istaknuto · {days}d
      </span>
    );
  }

  return (
    <>
      <Helmet><title>Moji oglasi — e-mina.eu</title></Helmet>
      <div className="max-w-5xl mx-auto px-4 py-10" data-testid="my-listings-page">
        <h1 className="text-3xl font-bold text-gray-900 dark:text-gray-100 mb-2">Moji oglasi</h1>
        <p className="text-gray-500 dark:text-gray-400 mb-8 text-sm">Pregledajte i upravljajte vlastitim oglasima u Oglasniku i Poslu. Možete istaknuti oglas na vrh popisa za simboličnu naknadu.</p>

        {loading ? (
          <div className="flex justify-center py-20"><Loader2 className="animate-spin text-gray-400" /></div>
        ) : (
          <>
            <section className="mb-10" data-testid="my-classifieds">
              <h2 className="text-lg font-bold text-gray-900 dark:text-gray-100 mb-3 flex items-center gap-2"><Tag size={16} /> Oglasnik ({classifieds.length})</h2>
              {classifieds.length === 0 ? (
                <p className="text-sm text-gray-500 dark:text-gray-400">Još nemate predanih oglasa. <Link to="/oglasnik/predaj" className="text-red-600">Predaj prvi oglas →</Link></p>
              ) : (
                <ul className="divide-y divide-gray-200 dark:divide-gray-800 bg-white dark:bg-[#111827] rounded-xl border border-gray-200 dark:border-gray-800">
                  {classifieds.map(c => {
                    const s = STATUS_LABEL[c.status] || { text: c.status, className: 'bg-gray-100 text-gray-700' };
                    const promoted = isPromoted(c);
                    return (
                      <li key={c.id} className={`p-4 flex items-center gap-3 ${promoted ? 'bg-amber-50/40 dark:bg-amber-900/10' : ''}`} data-testid={`my-classified-${c.id}`}>
                        {c.images?.[0] && <img src={c.images[0]} alt="" className="w-14 h-14 object-cover rounded" />}
                        <div className="flex-1 min-w-0">
                          <Link to={`/oglasnik/${c.id}`} className="font-semibold text-gray-900 dark:text-gray-100 hover:text-red-600 line-clamp-1">{c.title}</Link>
                          <div className="text-xs text-gray-500 mt-0.5 flex items-center gap-2 flex-wrap">
                            <span className={`px-2 py-0.5 rounded-full ${s.className}`}>{s.text}</span>
                            {promoted && <PromoteBadge until={c.promoted_until} />}
                            {c.location && <span>{c.location}</span>}
                            {c.price_eur != null && <span>{c.price_eur.toLocaleString('hr')} €</span>}
                          </div>
                        </div>
                        {c.status === 'active' && (
                          <button onClick={() => setPromoteFor({ kind: 'classifieds', item: c })}
                            className="px-3 py-1.5 text-xs font-bold rounded bg-amber-500 hover:bg-amber-600 text-white flex items-center gap-1"
                            data-testid={`promote-classified-${c.id}`}>
                            <Sparkles size={12} /> {promoted ? 'Produlji' : 'Istakni'}
                          </button>
                        )}
                        <button onClick={() => deleteItem('classifieds', c.id)} className="p-2 text-red-500 hover:text-red-700" aria-label="Obriši" data-testid={`delete-classified-${c.id}`}><Trash2 size={16} /></button>
                      </li>
                    );
                  })}
                </ul>
              )}
            </section>

            <section className="mt-10" data-testid="my-ad-placements">
              <h2 className="text-lg font-bold text-gray-900 dark:text-gray-100 mb-3 flex items-center gap-2">
                <Megaphone size={16} /> Plaćeni oglasi ({adPlacements.length})
              </h2>
              {adPlacements.length === 0 ? (
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  Još nemate plaćenih oglasa. <Link to="/oglasavanje" className="text-red-600">Kreiraj kampanju →</Link>
                </p>
              ) : (
                <ul className="space-y-3" data-testid="my-ad-placements-list">
                  {adPlacements.map(p => (
                    <MyAdPlacementCard key={p.id} placement={p} getToken={getAccessTokenSilently} onChanged={reload} />
                  ))}
                </ul>
              )}
            </section>

            <section className="mt-10" data-testid="my-messages">
              <h2 className="text-lg font-bold text-gray-900 dark:text-gray-100 mb-3 flex items-center gap-2"><MessageSquare size={16} /> Poruke ({conversations.length})</h2>
              {conversations.length === 0 ? (
                <p className="text-sm text-gray-500 dark:text-gray-400">Nema novih poruka.</p>
              ) : (
                <div className="grid md:grid-cols-[300px,1fr] gap-3 bg-white dark:bg-[#111827] rounded-xl border border-gray-200 dark:border-gray-800 p-3">
                  <div className="space-y-1 max-h-[60vh] overflow-y-auto">
                    {conversations.map(c => (
                      <button key={c.conversation_id} onClick={() => openConv(c)}
                              className={`w-full text-left p-2 rounded text-sm ${activeConv?.conversation_id === c.conversation_id ? 'bg-amber-100 dark:bg-amber-900/30' : 'hover:bg-gray-100 dark:hover:bg-gray-800'}`}
                              data-testid={`conv-btn-${c.conversation_id}`}>
                        <div className="flex items-center justify-between text-[10px] uppercase text-gray-500">
                          <span>{c.listing_type === 'classified' ? 'Oglas' : 'Posao'}</span>
                          {c.unread > 0 && <span className="bg-red-500 text-white px-1.5 py-0.5 rounded-full text-[9px]">{c.unread}</span>}
                        </div>
                        <div className="font-semibold line-clamp-1 text-gray-900 dark:text-gray-100">{c.listing_title}</div>
                        <div className="text-[11px] text-gray-500 line-clamp-1">{c.last_message}</div>
                      </button>
                    ))}
                  </div>
                  <div className="border-l border-gray-200 dark:border-gray-800 pl-3 min-h-[40vh] flex flex-col" data-testid="conv-pane">
                    {!activeConv ? (
                      <p className="text-sm text-gray-500 m-auto">Odaberite poruku.</p>
                    ) : (
                      <>
                        <div className="flex-1 overflow-y-auto space-y-2 mb-3 max-h-[50vh]">
                          {convMessages.map(m => (
                            <div key={m.id} className="text-sm bg-gray-50 dark:bg-gray-800 rounded p-2" data-testid={`my-msg-${m.id}`}>
                              <div className="flex justify-between text-[10px] text-gray-500 mb-0.5">
                                <span>{m.sender_email}</span>
                                <span>{(m.created_at || '').slice(0, 16).replace('T', ' ')}</span>
                              </div>
                              <p className="whitespace-pre-wrap">{m.body}</p>
                            </div>
                          ))}
                        </div>
                        <div className="flex gap-2">
                          <input value={reply} onChange={e => setReply(e.target.value)}
                                 placeholder="Vaš odgovor…"
                                 onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendReply(); } }}
                                 className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-700 bg-transparent dark:text-white rounded text-sm"
                                 data-testid="conv-reply-input" />
                          <button onClick={sendReply} className="px-3 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded text-sm inline-flex items-center gap-1" data-testid="conv-reply-send">
                            <Send size={14} /> Pošalji
                          </button>
                        </div>
                      </>
                    )}
                  </div>
                </div>
              )}
            </section>

            <section data-testid="my-jobs">
              <h2 className="text-lg font-bold text-gray-900 dark:text-gray-100 mb-3 flex items-center gap-2"><Briefcase size={16} /> Posao ({jobs.length})</h2>
              {jobs.length === 0 ? (
                <p className="text-sm text-gray-500 dark:text-gray-400">Još nemate predanih oglasa za posao. <Link to="/posao/predaj" className="text-red-600">Predaj oglas →</Link></p>
              ) : (
                <ul className="divide-y divide-gray-200 dark:divide-gray-800 bg-white dark:bg-[#111827] rounded-xl border border-gray-200 dark:border-gray-800">
                  {jobs.map(j => {
                    const s = STATUS_LABEL[j.status] || { text: j.status, className: 'bg-gray-100 text-gray-700' };
                    const promoted = isPromoted(j);
                    return (
                      <li key={j.id} className={`p-4 flex items-center gap-3 ${promoted ? 'bg-amber-50/40 dark:bg-amber-900/10' : ''}`} data-testid={`my-job-${j.id}`}>
                        {j.company_logo_url && <img src={j.company_logo_url} alt="" className="w-12 h-12 object-contain rounded bg-gray-50 dark:bg-gray-800" />}
                        <div className="flex-1 min-w-0">
                          <Link to={`/posao/${j.id}`} className="font-semibold text-gray-900 dark:text-gray-100 hover:text-red-600 line-clamp-1">{j.title}</Link>
                          <p className="text-xs text-gray-500 mt-0.5">{j.company} {j.location && `· ${j.location}`}</p>
                          <div className="mt-1 flex items-center gap-2">
                            <span className={`inline-block px-2 py-0.5 rounded-full text-[10px] ${s.className}`}>{s.text}</span>
                            {promoted && <PromoteBadge until={j.promoted_until} />}
                          </div>
                        </div>
                        {j.status === 'active' && (
                          <button onClick={() => setPromoteFor({ kind: 'jobs', item: j })}
                            className="px-3 py-1.5 text-xs font-bold rounded bg-amber-500 hover:bg-amber-600 text-white flex items-center gap-1"
                            data-testid={`promote-job-${j.id}`}>
                            <Sparkles size={12} /> {promoted ? 'Produlji' : 'Istakni'}
                          </button>
                        )}
                        <button onClick={() => deleteItem('jobs', j.id)} className="p-2 text-red-500 hover:text-red-700" aria-label="Obriši" data-testid={`delete-job-${j.id}`}><Trash2 size={16} /></button>
                      </li>
                    );
                  })}
                </ul>
              )}
            </section>
          </>
        )}

        {promoteFor && (
          <PromoteModal kind={promoteFor.kind} item={promoteFor.item}
            onClose={() => setPromoteFor(null)}
            onPaid={() => { setPromoteFor(null); reload(); }} />
        )}
      </div>
    </>
  );
}
